import { useState } from 'react';
import type { ParsedJsonUpload } from '../types';
import { usePipelineStepRunner } from './usePipelineStepRunner';
import { usePipelineStepState } from './usePipelineStepState';
import { usePipelineUploads } from './usePipelineUploads';

type UsePipelineDebugOptions = {
  organizationId: string;
  assetId: string;
};

export function usePipelineDebug({
  organizationId,
  assetId,
}: UsePipelineDebugOptions) {
  const [uploads, setUploads] = useState<ParsedJsonUpload[]>([]);

  const stepState = usePipelineStepState({
    organizationId,
    assetId,
    uploads,
  });

  const uploadHandlers = usePipelineUploads({
    organizationId,
    assetId,
    setUploads,
    setSteps: stepState.setSteps,
    resetPipeline: stepState.resetPipeline,
    updateStepInput: stepState.updateStepInput,
  });

  const runner = usePipelineStepRunner({
    organizationId,
    assetId,
    uploads,
    setSteps: stepState.setSteps,
    activeStepIndex: stepState.activeStepIndex,
    setActiveStepIndex: stepState.setActiveStepIndex,
    runMode: stepState.runMode,
  });

  return {
    runMode: stepState.runMode,
    setRunMode: stepState.setRunMode,
    activeStepIndex: stepState.activeStepIndex,
    setActiveStepIndex: stepState.setActiveStepIndex,
    activeStepId: stepState.activeStepId,
    activeStep: stepState.activeStep,
    uploads,
    steps: stepState.steps,
    pipelineSummary: stepState.pipelineSummary,
    canRun: stepState.canRun,
    ingestFiles: uploadHandlers.ingestFiles,
    ingestStepInputFiles: uploadHandlers.ingestStepInputFiles,
    removeUpload: uploadHandlers.removeUpload,
    runStep: runner.runStep,
    runFromActiveStep: runner.runFromActiveStep,
    continuePipeline: runner.continuePipeline,
    updateStepInput: stepState.updateStepInput,
    updateStepOutput: stepState.updateStepOutput,
    downloadStepInput: stepState.downloadStepInput,
    downloadStepOutput: stepState.downloadStepOutput,
    copyStepInput: stepState.copyStepInput,
    copyStepOutput: stepState.copyStepOutput,
    resetPipeline: stepState.resetPipeline,
  };
}
